import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import React, { useMemo, useState } from 'react';

const appearance = {
  theme: 'stripe',
  rules: {
    '.AccordionItem': {
      border: 'none',
      boxShadow: 'none',
    },
    '.Block': {
      border: 'none',
      boxShadow: 'none',
    },
    '.Tab': {
      border: 'none',
      boxShadow: 'none',
    },
  },
};

function pushPay(pushEventTo, target, confirmationToken) {
  return new Promise(resolve => {
    const targetEl = document.getElementById(target);
    if (!targetEl || !pushEventTo) {
      resolve({ ok: false, error: 'Payment form is unavailable.' });
      return;
    }

    const pushed = pushEventTo(targetEl, 'pay', { confirmation_token: confirmationToken }, reply => {
      resolve(reply || { ok: false, error: 'Payment was not completed.' });
    });

    if (pushed === undefined) {
      resolve({ ok: false, error: 'Payment form is unavailable.' });
    }
  });
}

function payerError(error) {
  const message = typeof error === 'string' ? error : error?.message || '';

  if (
    message.includes('elements.submit') ||
    message.includes('createConfirmationToken') ||
    message.includes('Integration guide')
  ) {
    return null;
  }

  return message || 'Payment was not completed.';
}

function paymentElementOptions(billingName) {
  const options = {
    wallets: { link: 'never' },
  };

  if (!billingName) return options;

  return {
    ...options,
    defaultValues: {
      billingDetails: { name: billingName },
    },
    fields: {
      billingDetails: { name: 'never' },
    },
  };
}

function Checkout({ target, pushEventTo, billingName }) {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState(null);
  const [paying, setPaying] = useState(false);
  const [complete, setComplete] = useState(false);

  async function pay() {
    if (!stripe || !elements || paying || !complete) return;

    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setError(payerError(submitError));
        setPaying(false);
        return;
      }

      setPaying(true);
      setError(null);

      const params = { return_url: window.location.href };

      if (billingName) {
        params.payment_method_data = { billing_details: { name: billingName } };
      }

      const { error: tokenError, confirmationToken } = await stripe.createConfirmationToken({
        elements,
        params,
      });

      if (tokenError || !confirmationToken) {
        setError(payerError(tokenError));
        setPaying(false);
        return;
      }

      const reply = await pushPay(pushEventTo, target, confirmationToken.id);
      if (!reply.ok) {
        setError(payerError(reply.error));
        setPaying(false);
      }
    } catch (payError) {
      setError(payerError(payError));
      setPaying(false);
    }
  }

  return (
    <div className='space-y-3'>
      <PaymentElement options={paymentElementOptions(billingName)} onChange={event => setComplete(event.complete)} />
      {error ? <p className='text-sm text-red-600'>{error}</p> : null}
      <button
        type='button'
        onClick={pay}
        disabled={!stripe || !elements || paying || !complete}
        className='shadow-xs inline-flex items-center justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50'
      >
        {paying ? 'Paying...' : 'Pay'}
      </button>
    </div>
  );
}

export function StripePayment({ publishableKey, stripeAccount, amount, currency, billingName, target, pushEventTo }) {
  const stripePromise = useMemo(
    () =>
      loadStripe(publishableKey, {
        stripeAccount,
        developerTools: { assistant: { enabled: false } },
      }),
    [publishableKey, stripeAccount],
  );

  if (!publishableKey || !stripeAccount || !amount || !currency) {
    return null;
  }

  return (
    <Elements stripe={stripePromise} options={{ mode: 'payment', amount, currency, appearance }}>
      <Checkout target={target} pushEventTo={pushEventTo} billingName={billingName} />
    </Elements>
  );
}
